import React, { Suspense, useEffect, useState } from 'react';
import {
  useLocation,
  Navigate,
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom';
import { CssVarsProvider } from '@mui/joy/styles';
import { ToastContainer } from 'react-toastify';
import { ROUTES } from './config/routes';
import HomePage from './pages/Home/HomePage';
import MarketplacePage from './pages/MarketplacePage';
import CartPage from './pages/CartPage';
import LoginPage from './pages/LoginPage';
import { useAppContext } from './context/AppContext';
import PurchaseConfirmationPage from './pages/PurchaseConfirmationPage';
import LogoutPage from './pages/LogoutPage';
import EulaPage from './pages/EulaPage';
import PrivacyPage from './pages/PrivacyPage';
import AccountPage from './pages/AccountPage';
import LoadingOverlay from './components/LoadingOverlay';
import { Helmet } from 'react-helmet';

import 'react-toastify/dist/ReactToastify.css';
import NotFound from './components/NotFound';
import theme from './theme';
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import FAQPage from './pages/FAQPage';
import BrandsCheckoutPage from './pages/BrandsCheckoutPage';
import BrandsManagerPage from './pages/BrandsManagerPage';
import RegularWebCardPage from './pages/WebCard/Regular/RegularWebCardPage';
import ReferredToWebCardPage from './pages/WebCard/Referred/ReferredToWebCardPage';
import { getCurrentUserIP, sendLogsToServer } from './utils/log';
import axios from 'axios';
import { isValidSession } from './services/jwt';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
  authDomain: 'grounded-access-142814.firebaseapp.com',
  databaseURL: 'https://grounded-access-142814.firebaseio.com',
  projectId: 'grounded-access-142814',
  storageBucket: 'grounded-access-142814.appspot.com',
  messagingSenderId: '25660083253',
  appId: '1:25660083253:web:43641e9a4d6ff6e552757c',
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);

const RequireAuth = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [isValid, setIsValid] = useState(false);
  const { setSessionInfo } = useAppContext();

  useEffect(() => {
    const userAgentString = navigator.userAgent;
    getCurrentUserIP().then((ip) => {
      const info = {
        ip,
        userAgent: userAgentString,
      };
      setSessionInfo(info);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    async function checkIfSessionValid() {
      if (await isValidSession()) {
        setIsValid(true);
      }
      setLoading(false);
    }
    checkIfSessionValid();
  }, []);

  if (loading) {
    return <LoadingOverlay />;
  }

  return isValid ? (
    children
  ) : (
    <Navigate
      to={ROUTES.LOGIN}
      replace
      state={{ path: `${location.pathname}` }}
    />
  );
};

/** TODO: switch to using an <Outlet/> component from react router for nested content
 *  @see - https://reactrouter.com/en/main/components/outlet
 */
function App() {
  // Send logs to the server when the user leaves the page
  document.onvisibilitychange = function () {
    if (document.visibilityState === 'hidden') {
      sendLogsToServer();
    }
  };

  const { setDb, setStorage } = useAppContext();

  /* Public routes */
  const router = createBrowserRouter([
    {
      path: '/',
      element: <HomePage />,
      index: true,
      errorElement: <NotFound />,
    },
    { path: ROUTES.LOGIN, element: <LoginPage /> },
    { path: ROUTES.LOGOUT, element: <LogoutPage /> },
    { path: ROUTES.EULA, element: <EulaPage /> },
    { path: ROUTES.PRIVACY, element: <PrivacyPage /> },
    { path: ROUTES.FAQ, element: <FAQPage /> },
    { path: ROUTES.BRANDS_CHECKOUT, element: <BrandsCheckoutPage /> },

    /* Authenticated routes */
    {
      path: ROUTES.MARKETPLACE,
      element: (
        <RequireAuth>
          <MarketplacePage />
        </RequireAuth>
      ),
    },
    {
      path: ROUTES.ACCOUNT,
      element: (
        <RequireAuth>
          <AccountPage />
        </RequireAuth>
      ),
    },
    {
      path: ROUTES.CART,
      element: (
        <RequireAuth>
          <CartPage />
        </RequireAuth>
      ),
    },
    {
      path: ROUTES.PURCHASE_CONFIRMATION,
      element: (
        <RequireAuth>
          <PurchaseConfirmationPage />
        </RequireAuth>
      ),
    },
    {
      path: ROUTES.BRANDS_DASHBOARD,
      element: (
        <RequireAuth>
          <BrandsManagerPage />
        </RequireAuth>
      ),
    },

    // TODO: possible future routes
    // <Route path:{ROUTES.SIGN_UP} element:{<SignUpPage />} /> */

    // Default to looking up a profile
    { path: ROUTES.REFERRED_TO_PROFILE, element: <ReferredToWebCardPage /> },
    {
      path: ROUTES.PUBLIC_PROFILE,
      element: (
        <Suspense fallback={<LoadingOverlay />}>
          <RegularWebCardPage />
        </Suspense>
      ),
    },
  ]);

  useEffect(() => {
    setDb(db);
    setStorage(storage);
  }, []);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>& - Move The World Toward Joy</title>
      </Helmet>
      <CssVarsProvider theme={theme}>
        <RouterProvider router={router} />
        <ToastContainer limit={1} />
      </CssVarsProvider>
    </>
  );
}

export default App;
